import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import SignInGate from '@/components/SignInGate';
import ScrollToTop from './components/ScrollToTop';
import { ThemeProvider } from './components/ThemeProvider';
import AuthCallback from './pages/AuthCallback';
import Activate from './pages/Activate';
import ResetPassword from './pages/ResetPassword';
// Add page imports here
import Organogram from './pages/Organogram';

const AuthenticatedApp = () => {
  const { isAuthenticated, isLoadingAuth, authError } = useAuth();

  // Show loading spinner while the Blocks IAM session is being resolved from
  // the tokens in localStorage.
  if (isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // A signed-in user whose account isn't provisioned for this app.
  if (authError?.type === 'user_not_registered') {
    return <UserNotRegisteredError />;
  }

  // Gate the app behind Blocks IAM auth. Anonymous users get the sign-in
  // screen, which starts the OIDC login flow when they click "Sign in".
  // (We don't auto-redirect — the user explicitly initiates login.)
  if (!isAuthenticated) {
    return <SignInGate />;
  }

  // Render the main app
  return (
    <Routes>
      {/* Add your page Route elements here */}
      <Route path="/" element={<Organogram />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};


function App() {

  return (
    <AuthProvider>
      <ThemeProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            <ScrollToTop />
            <Routes>
              {/* Blocks IAM OIDC callback — handled outside the auth gate */}
              <Route path="/callback" element={<AuthCallback />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              {/* Blocks IAM account activation — unauthenticated (code is the credential) */}
              <Route path="/activate" element={<Activate />} />
              {/* Blocks IAM password reset — unauthenticated (recovery code is the credential).
                  Path must match `recoverAccountPath` in the tenant's POST /auth/config. */}
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/recover-account" element={<ResetPassword />} />
              <Route path="*" element={<AuthenticatedApp />} />
            </Routes>
          </Router>
          <Toaster />
        </QueryClientProvider>
      </ThemeProvider>
    </AuthProvider>
  )
}

export default App